import React, { useCallback, useEffect, useId, useState } from 'react';
import { Check, Loader2, Search, User } from 'lucide-react';
import { toast } from 'sonner';
import type { SubscriberRow } from '../Subscribers/types';
import { formatParticipantLine, parseParticipantLine } from '../../lib/transcriptParticipants';
import {
  searchConnections,
  subscriberBestEmail,
  subscriberBestName,
  subscriberDisplayLabel,
} from '../../lib/subscriberDisplay';
import { supabase } from '../../lib/supabase';
import { ModalBody, ModalFooter, ModalHeader, ModalOverlay, ModalPanel } from '../Common/ModalShell';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LinkMode = 'manual' | 'connection';

interface EditTranscriptParticipantModalProps {
  isOpen: boolean;
  transcriptId: string;
  participantIndex: number;
  participants: string[];
  onClose: () => void;
  onSaved: () => void;
}

const EditTranscriptParticipantModal: React.FC<EditTranscriptParticipantModalProps> = ({
  isOpen,
  transcriptId,
  participantIndex,
  participants,
  onClose,
  onSaved,
}) => {
  const titleId = useId();
  const participantLine = participants[participantIndex] ?? '';
  const parsed = parseParticipantLine(participantLine);

  const [mode, setMode] = useState<LinkMode>('manual');
  const [name, setName] = useState(parsed.name);
  const [email, setEmail] = useState(parsed.email);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SubscriberRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<SubscriberRow | null>(null);
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    const next = parseParticipantLine(participantLine);
    setMode(next.email ? 'manual' : 'connection');
    setName(next.name);
    setEmail(next.email);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedConnection(null);
    setSaving(false);
  }, [participantLine]);

  useEffect(() => {
    if (isOpen) resetForm();
  }, [isOpen, resetForm]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (mode !== 'connection' || q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = window.setTimeout(() => {
      void searchConnections(q, 10)
        .then((rows) => setSearchResults(rows))
        .catch((e: unknown) => {
          setSearchResults([]);
          toast.error(e instanceof Error ? e.message : 'Connection search failed');
        })
        .finally(() => setSearchLoading(false));
    }, 250);

    return () => window.clearTimeout(timer);
  }, [mode, searchQuery]);

  const pickConnection = (row: SubscriberRow) => {
    setSelectedConnection(row);
    const pickedEmail = subscriberBestEmail(row);
    const pickedName = subscriberBestName(row);
    if (pickedEmail) setEmail(pickedEmail);
    if (pickedName && (!name.trim() || name.trim() === parsed.name.trim())) {
      setName(pickedName);
    }
  };

  const handleSave = async () => {
    const emailTrim = email.trim();
    const nameTrim = name.trim();

    if (!emailTrim) {
      toast.error('Enter an email or pick a CRM connection.');
      return;
    }
    if (!EMAIL_RE.test(emailTrim)) {
      toast.error('Enter a valid email address.');
      return;
    }

    setSaving(true);
    try {
      const updated = [...participants];
      updated[participantIndex] = formatParticipantLine(nameTrim || parsed.name, emailTrim);

      const { error } = await supabase
        .from('transcripts')
        .update({
          participants: updated,
          updated_at: new Date().toISOString(),
        })
        .eq('id', transcriptId);

      if (error) throw error;

      toast.success('Attendee email updated');
      onSaved();
      onClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to update attendee');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const displayLabel = name.trim() || parsed.name || 'Attendee';

  return (
    <ModalOverlay onClose={saving ? undefined : onClose} closeOnBackdropClick={!saving} zIndexClass="z-[60]">
      <ModalPanel maxWidthClass="max-w-lg" titleId={titleId}>
        <ModalHeader
          title="Link attendee email"
          description={displayLabel}
          onClose={saving ? undefined : onClose}
          titleId={titleId}
        />
        <ModalBody className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Set or correct the email for this attendee so future CRM lookups match by address.
          </p>

          <div className="flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-50 dark:bg-gray-900/50">
            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('manual')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'manual'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Enter email
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('connection')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                mode === 'connection'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              Pick from CRM
            </button>
          </div>

          <div>
            <label htmlFor="participant-name" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Name
            </label>
            <input
              id="participant-name"
              type="text"
              value={name}
              disabled={saving}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
              placeholder="Attendee name"
            />
          </div>

          {mode === 'manual' ? (
            <div>
              <label htmlFor="participant-email" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                Email
              </label>
              <input
                id="participant-email"
                type="email"
                value={email}
                disabled={saving}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                placeholder="name@company.com"
                autoComplete="off"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="connection-search" className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                Search connections
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  id="connection-search"
                  type="search"
                  value={searchQuery}
                  disabled={saving}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 pl-9 pr-3 py-2 text-sm text-gray-900 dark:text-white"
                  placeholder="Name or email…"
                  autoComplete="off"
                />
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 max-h-52 overflow-y-auto">
                {searchLoading ? (
                  <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Searching…
                  </div>
                ) : searchQuery.trim().length < 2 ? (
                  <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">Type at least 2 characters to search.</p>
                ) : searchResults.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-gray-500 dark:text-gray-400">No connections found.</p>
                ) : (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                    {searchResults.map((row) => {
                      const rowEmail = subscriberBestEmail(row);
                      const selected = selectedConnection?.id === row.id;
                      return (
                        <li key={String(row.id)}>
                          <button
                            type="button"
                            disabled={saving || !rowEmail}
                            onClick={() => pickConnection(row)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                              selected
                                ? 'bg-emerald-50 dark:bg-emerald-950/40'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            }`}
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                              <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {subscriberDisplayLabel(row)}
                              </p>
                              {rowEmail ? (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{rowEmail}</p>
                              ) : (
                                <p className="text-xs text-amber-600 dark:text-amber-400">No email on file</p>
                              )}
                            </div>
                            {selected ? <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" /> : null}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {email.trim() ? (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Selected email: <span className="font-medium text-gray-700 dark:text-gray-200">{email.trim()}</span>
                </p>
              ) : null}
            </div>
          )}
        </ModalBody>
        <ModalFooter className="flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save
          </button>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default EditTranscriptParticipantModal;
